import {
	Button,
	ChipModal,
	ChipModalBody,
	ChipModalField,
	ChipModalFooter,
	ChipModalHeader,
	DropdownMenu,
	DropdownMenuContent,
	DropdownMenuItem,
	DropdownMenuSeparator,
	DropdownMenuTrigger,
	Table,
	TableBody,
	TableCell,
	TableHead,
	TableHeader,
	TableRow,
} from "@ondit/ui";
import { ChevronDown } from "lucide-react";
import { useState } from "react";
import { Link, useParams } from "react-router";
import {
	Async,
	Badge,
	Empty,
	KV,
	PageHead,
	Pager,
	SearchBox,
	Segmented,
	Status,
	useAction,
	useConfirm,
} from "@/components/ui";
import { useI18n, useT } from "@/i18n";
import { api, type Me, type Plan, type User, type UserDetail } from "@/lib/api";
import * as fmt from "@/lib/format";
import { useAsync, useDebounced, usePaged } from "@/lib/hooks";

export function UsersPage() {
	const t = useT();
	const { locale } = useI18n();
	const [q, setQ] = useState("");
	const [filter, setFilter] = useState<"" | "admins" | "unverified">("");
	const query = useDebounced(q);
	const { state, rows, hasMore, loadingMore, more } = usePaged(
		(offset, limit) => api.users({ q: query, filter, limit, offset }),
		(page) => page.users,
		[query, filter],
	);

	return (
		<>
			<PageHead title={t("users.title")} help={t("users.lede")} />
			<div className="table-tools">
				<SearchBox value={q} onChange={setQ} />
				<Segmented
					value={filter}
					onChange={setFilter}
					options={[
						{ value: "", label: t("state.all") },
						{ value: "admins", label: t("users.filter.admins") },
						{ value: "unverified", label: t("users.filter.unverified") },
					]}
				/>
			</div>
			<Async state={state}>
				{({ total }) =>
					rows.length === 0 ? (
						<Empty />
					) : (
						<>
							<div className="table-scroll">
								<Table className="text-[length:var(--t-sm)]">
									<TableHeader>
										<TableRow>
											<TableHead>{t("users.email")}</TableHead>
											<TableHead>{t("users.name")}</TableHead>
											<TableHead>{t("state.status")}</TableHead>
											<TableHead>{t("users.providers")}</TableHead>
											<TableHead className="num text-right whitespace-nowrap">{t("users.orgs")}</TableHead>
											<TableHead className="num text-right whitespace-nowrap">{t("users.sessions")}</TableHead>
											<TableHead>{t("state.created")}</TableHead>
										</TableRow>
									</TableHeader>
									<TableBody>
										{rows.map((u) => (
											<TableRow key={u.id}>
												<TableCell>
													<Link className="cell-summary" title={u.email} to={`/users/${u.id}`}>
														{u.email}
													</Link>
													<span className="sub mono cell-summary" title={u.id}>
														{u.id}
													</span>
												</TableCell>
												<TableCell>{u.name ?? "—"}</TableCell>
												<TableCell>
													<div className="chips">
														{u.is_admin ? <Badge tone="neutral">{t("users.admin")}</Badge> : null}
														{u.email_verified_at ? (
															<Badge tone="success">{t("users.verified")}</Badge>
														) : (
															<Badge tone="warning">{t("users.unverified")}</Badge>
														)}
													</div>
												</TableCell>
												<TableCell>
													{[u.has_password ? "password" : null, ...u.providers].filter(Boolean).join(", ") || "—"}
												</TableCell>
												<TableCell className="num text-right whitespace-nowrap">{u.orgs}</TableCell>
												<TableCell className="num text-right whitespace-nowrap">{u.sessions}</TableCell>
												<TableCell className="whitespace-nowrap">{fmt.date(locale, u.created_at)}</TableCell>
											</TableRow>
										))}
									</TableBody>
								</Table>
							</div>
							<Pager shown={rows.length} total={total} hasMore={hasMore} busy={loadingMore} onMore={more} />
						</>
					)
				}
			</Async>
		</>
	);
}

export function UserPage() {
	const { id = "" } = useParams();
	const t = useT();
	const { locale } = useI18n();
	const state = useAsync<UserDetail>(() => api.user(id), [id]);
	const me = useAsync<Me>(() => api.me(), []);
	const plans = useAsync(() => api.plans(), []);
	const confirm = useConfirm();
	const { run, busy } = useAction();
	const [open, setOpen] = useState<"password" | "credits" | "level" | null>(null);
	const close = () => setOpen(null);

	return (
		<Async state={state}>
			{(d) => {
				const u: User = d.user;
				const done = () => {
					close();
					state.reload();
				};
				const isBootstrap = me.data?.bootstrap_email?.toLowerCase() === u.email.toLowerCase();
				const isMe = me.data?.user.user_id === u.id;
				return (
					<>
						<PageHead title={u.email} lede={u.name ?? undefined} crumbs={[{ to: "/users", label: t("users.title") }]}>
							{!u.email_verified_at ? (
								<Button
									variant="default"
									type="button"
									disabled={busy}
									onClick={() =>
										run(
											() => api.verifyUser(u.id),
											() => t("state.saved"),
										).then(state.reload)
									}
								>
									{t("users.verify")}
								</Button>
							) : null}
							<DropdownMenu>
								<DropdownMenuTrigger asChild>
									<Button variant="default" type="button" disabled={busy}>
										{t("state.actions")}
										<ChevronDown />
									</Button>
								</DropdownMenuTrigger>
								<DropdownMenuContent align="end">
									<DropdownMenuItem onSelect={() => setOpen("credits")}>{t("users.credits")}</DropdownMenuItem>
									<DropdownMenuItem onSelect={() => setOpen("level")}>{t("users.level")}</DropdownMenuItem>
									<DropdownMenuSeparator />
									<DropdownMenuItem onSelect={() => setOpen("password")}>{t("users.resetPassword")}</DropdownMenuItem>
									<DropdownMenuItem
										className={u.is_admin ? "text-danger" : undefined}
										disabled={isMe || (u.is_admin && isBootstrap)}
										onSelect={async () => {
											const ok = await confirm(
												t(u.is_admin ? "users.demote" : "users.promote"),
												t(u.is_admin ? "users.demoteConfirm" : "users.promoteConfirm", { email: u.email }),
												u.is_admin,
											);
											if (ok)
												run(
													() => api.setAdmin(u.id, !u.is_admin),
													() => t("state.saved"),
												).then(state.reload);
										}}
									>
										{t(u.is_admin ? "users.demote" : "users.promote")}
									</DropdownMenuItem>
									<DropdownMenuItem
										onSelect={async () => {
											const ok = await confirm(t("users.revoke"), t("users.revokeConfirm", { email: u.email }), true);
											if (ok)
												run(
													() => api.revokeSessions(u.id),
													(r) => t("users.revoked", { panel: r.panel, console: r.console }),
												).then(state.reload);
										}}
									>
										{t("users.revoke")}
									</DropdownMenuItem>
								</DropdownMenuContent>
							</DropdownMenu>
						</PageHead>

						<div className="grid-2 detail-grid">
							<div className="card">
								<KV
									items={[
										[t("state.id"), <span className="mono">{u.id}</span>],
										[
											t("state.status"),
											<div className="chips">
												{u.is_admin ? <Badge tone="neutral">{t("users.admin")}</Badge> : null}
												{u.email_verified_at ? (
													<Badge tone="success">{t("users.verified")}</Badge>
												) : (
													<Badge tone="warning">{t("users.unverified")}</Badge>
												)}
												{isBootstrap ? <Badge tone="outline">{t("users.bootstrap")}</Badge> : null}
											</div>,
										],
										[t("users.password"), t(u.has_password ? "state.yes" : "state.no")],
										[t("nav.language"), u.locale],
										[t("state.created"), fmt.dateTime(locale, u.created_at)],
										[t("users.sessions"), `${u.sessions} · ${t("users.consoleSessions", { n: d.admin_sessions })}`],
									]}
								/>
							</div>

							<div className="data-section data-section-wide">
								<h2>{t("users.orgs")}</h2>
								{d.orgs.length === 0 ? (
									<p className="muted">{t("state.none")}</p>
								) : (
									<Table className="text-[length:var(--t-sm)]">
										<TableHeader>
											<TableRow>
												<TableHead>{t("orgs.name")}</TableHead>
												<TableHead>{t("orgs.role")}</TableHead>
												<TableHead>{t("orgs.plan")}</TableHead>
												<TableHead className="num text-right whitespace-nowrap">{t("orgs.credits")}</TableHead>
											</TableRow>
										</TableHeader>
										<TableBody>
											{d.orgs.map((o) => (
												<TableRow key={o.id}>
													<TableCell>
														<Link to={`/orgs/${o.id}`}>{o.name}</Link>
														{o.paused ? <Badge tone="warning">{t("orgs.paused")}</Badge> : null}
													</TableCell>
													<TableCell>{o.role}</TableCell>
													<TableCell>
														{o.plan_key}
														{o.subscription_status ? (
															<span className="sub">
																<Status value={o.subscription_status} prefix="billing.status" />{" "}
																{fmt.date(locale, o.subscription_ends_at)}
															</span>
														) : null}
													</TableCell>
													<TableCell className="num text-right whitespace-nowrap">
														{fmt.number(locale, o.credits)}
													</TableCell>
												</TableRow>
											))}
										</TableBody>
									</Table>
								)}
							</div>

							<div className="card">
								<h2>{t("users.identities")}</h2>
								{d.identities.length === 0 ? (
									<p className="muted">{t("state.none")}</p>
								) : (
									<KV
										items={d.identities.map((i) => [
											i.provider,
											`${i.email ?? "—"} · ${fmt.date(locale, i.created_at)}`,
										])}
									/>
								)}
							</div>
							<div className="data-section data-section-wide">
								<h2>{t("users.actions")}</h2>
								{d.actions.length === 0 ? (
									<p className="muted">{t("state.none")}</p>
								) : (
									<Table className="text-[length:var(--t-sm)]">
										<TableBody>
											{d.actions.map((a) => (
												<TableRow key={a.id}>
													<TableCell className="mono cell-clip">{a.action}</TableCell>
													<TableCell className="mono cell-clip">{a.target ?? "—"}</TableCell>
													{/* Relative, not a timestamp: this is a summary card a third as wide as
													    the audit log, and a wrapped date was making every row five lines. */}
													<TableCell className="small cell-clip">{fmt.relative(locale, a.created_at)}</TableCell>
												</TableRow>
											))}
										</TableBody>
									</Table>
								)}
							</div>
						</div>

						{open === "password" ? (
							<PasswordModal user={u} bootstrap={isBootstrap} onClose={close} onDone={done} />
						) : null}
						{open === "credits" ? <CreditsModal detail={d} onClose={close} onDone={done} /> : null}
						{open === "level" ? (
							<LevelModal detail={d} plans={plans.data?.plans ?? []} onClose={close} onDone={done} />
						) : null}
					</>
				);
			}}
		</Async>
	);
}

/** Twenty characters from the browser's own randomness, no ambiguous glyphs. */
function generatePassword(): string {
	const alphabet = "abcdefghjkmnpqrstuvwxyzABCDEFGHJKLMNPQRSTUVWXYZ23456789";
	const bytes = new Uint8Array(20);
	crypto.getRandomValues(bytes);
	return Array.from(bytes, (b) => alphabet[b % alphabet.length]).join("");
}

function PasswordModal({
	user,
	bootstrap,
	onClose,
	onDone,
}: {
	user: User;
	bootstrap: boolean;
	onClose: () => void;
	onDone: () => void;
}) {
	const t = useT();
	const { run, busy } = useAction();
	const confirm = useConfirm();
	const [password, setPassword] = useState("");

	async function submit() {
		const ok = await confirm(t("users.resetPassword"), user.email, true);
		if (!ok) return;
		const r = await run(
			() => api.setPassword(user.id, password),
			(r) => t("users.passwordSet", { n: r.sessions_ended }),
		);
		if (r) onDone();
	}

	return (
		<ChipModal open dismissDisabled={busy} srTitle={t("users.resetPassword")} onOpenChange={onClose}>
			<ChipModalHeader onClose={onClose}>{t("users.resetPassword")}</ChipModalHeader>
			<ChipModalBody>
				<fieldset className="form" disabled={busy}>
					<ChipModalField
						type="input"
						title={t("users.newPassword")}
						mono
						required
						disabled={bootstrap}
						value={password}
						onChange={setPassword}
						/* Why the field is dead, which is a state and not an explanation. */
						hint={bootstrap ? t("users.bootstrapPassword") : t("users.resetPasswordHint")}
					/>
				</fieldset>
			</ChipModalBody>
			<ChipModalFooter
				cancelLabel={t("state.cancel")}
				onCancel={onClose}
				secondaryActions={
					bootstrap ? undefined : [{ label: t("users.generate"), onClick: () => setPassword(generatePassword()) }]
				}
				primaryAction={{
					label: t("users.resetPassword"),
					variant: "destructive",
					onClick: () => void submit(),
					disabled: busy || bootstrap || password.length < 8,
				}}
			/>
		</ChipModal>
	);
}

function CreditsModal({ detail, onClose, onDone }: { detail: UserDetail; onClose: () => void; onDone: () => void }) {
	const t = useT();
	const { locale } = useI18n();
	const { run, busy } = useAction();
	const confirm = useConfirm();
	const [org, setOrg] = useState(detail.orgs[0]?.id ?? "");
	const [amount, setAmount] = useState("1000");
	const [note, setNote] = useState("");

	async function submit() {
		const n = Number(amount);
		if (!org || !Number.isFinite(n) || n === 0) return;
		if (n < 0) {
			const name = detail.orgs.find((o) => o.id === org)?.name ?? org;
			const ok = await confirm(
				t("orgs.revoke"),
				t("orgs.revokeConfirm", { credits: fmt.number(locale, -n), name }),
				true,
			);
			if (!ok) return;
		}
		const r = await run(
			() =>
				n > 0
					? api.grant(org, { credits: n, note: note || undefined, expires_at: null })
					: api.revoke(org, { credits: -n, note: note || undefined }),
			(r) => t(n > 0 ? "orgs.granted" : "orgs.revoked", { balance: fmt.number(locale, r.balance) }),
		);
		if (r) onDone();
	}

	return (
		<ChipModal open dismissDisabled={busy} srTitle={t("users.credits")} onOpenChange={onClose}>
			<ChipModalHeader onClose={onClose}>{t("users.credits")}</ChipModalHeader>
			<ChipModalBody>
				<fieldset className="form" disabled={busy}>
					{detail.orgs.length === 0 ? (
						<p className="muted">{t("users.noOrgs")}</p>
					) : (
						<>
							<ChipModalField
								type="dropdown"
								title={t("users.forAccount")}
								value={org}
								onChange={setOrg}
								options={detail.orgs.map((o) => ({
									value: o.id,
									label: `${o.name} · ${fmt.number(locale, o.credits)}`,
								}))}
							/>
							<ChipModalField
								type="input"
								title={t("orgs.grantAmount")}
								hint={t("users.creditsHint")}
								required
								value={amount}
								onChange={setAmount}
							/>
							<ChipModalField
								type="input"
								title={t("state.note")}
								hint={t("state.noteHint")}
								required
								value={note}
								onChange={setNote}
							/>
						</>
					)}
				</fieldset>
			</ChipModalBody>
			<ChipModalFooter
				cancelLabel={t("state.cancel")}
				onCancel={onClose}
				primaryAction={{
					label: t("users.creditsApply"),
					variant: Number(amount) < 0 ? "destructive" : "primary",
					onClick: () => void submit(),
					disabled: busy || !org || !Number.isFinite(Number(amount)) || Number(amount) === 0 || !note.trim(),
				}}
			/>
		</ChipModal>
	);
}

function LevelModal({
	detail,
	plans,
	onClose,
	onDone,
}: {
	detail: UserDetail;
	plans: Plan[];
	onClose: () => void;
	onDone: () => void;
}) {
	const t = useT();
	const { locale } = useI18n();
	const { run, busy } = useAction();
	const confirm = useConfirm();
	const active = plans.filter((p) => p.active);
	const [org, setOrg] = useState(detail.orgs[0]?.id ?? "");
	const [plan, setPlan] = useState(detail.orgs[0]?.plan_key ?? "");
	const [months, setMonths] = useState("1");
	const [note, setNote] = useState("");
	const paid = active.find((p) => p.key === plan)?.price_usd_micros ?? 0;

	async function submit() {
		if (!org || !plan) return;
		// The hint alone said what the control does in general; the confirm has
		// to say what *this* press does, because that is the decision.
		const chosen = active.find((p) => p.key === plan);
		const name = detail.orgs.find((o) => o.id === org)?.name ?? org;
		const ok = await confirm(
			t("orgs.comp"),
			paid > 0
				? t("orgs.compConfirm", {
						name,
						plan: (locale === "zh" ? chosen?.name_zh : chosen?.name_en) ?? plan,
						months,
					})
				: t("orgs.compConfirmFree", {
						name,
						plan: (locale === "zh" ? chosen?.name_zh : chosen?.name_en) ?? plan,
					}),
			paid <= 0,
		);
		if (!ok) return;
		const r = await run(
			() => api.setPlan(org, { plan_key: plan, months: Number(months), note: note || undefined }),
			(r) =>
				r.period_end
					? t("orgs.comped", { plan, until: fmt.date(locale, r.period_end), credits: r.credits_granted })
					: t("orgs.planSet", { plan }),
		);
		if (r) onDone();
	}

	return (
		<ChipModal open dismissDisabled={busy} srTitle={t("users.level")} onOpenChange={onClose}>
			<ChipModalHeader onClose={onClose}>{t("users.level")}</ChipModalHeader>
			<ChipModalBody>
				<fieldset className="form" disabled={busy}>
					{detail.orgs.length === 0 ? (
						<p className="muted">{t("users.noOrgs")}</p>
					) : (
						<>
							<ChipModalField
								type="dropdown"
								title={t("users.forAccount")}
								value={org}
								onChange={(next) => {
									setOrg(next);
									setPlan(detail.orgs.find((o) => o.id === next)?.plan_key ?? plan);
								}}
								options={detail.orgs.map((o) => ({ value: o.id, label: `${o.name} · ${o.plan_key}` }))}
							/>
							<ChipModalField
								type="dropdown"
								title={t("orgs.plan")}
								hint={t("orgs.compHint")}
								value={plan}
								onChange={setPlan}
								options={active.map((p) => ({
									value: p.key,
									label: `${locale === "zh" ? p.name_zh : p.name_en} (${p.key})`,
								}))}
							/>
							<ChipModalField
								type="input"
								title={t("orgs.compMonths")}
								inputMode="numeric"
								disabled={paid <= 0}
								value={months}
								onChange={setMonths}
							/>
							<ChipModalField type="input" title={t("state.note")} value={note} onChange={setNote} />
						</>
					)}
				</fieldset>
			</ChipModalBody>
			<ChipModalFooter
				cancelLabel={t("state.cancel")}
				onCancel={onClose}
				primaryAction={{
					label: t("orgs.comp"),
					onClick: () => void submit(),
					disabled: busy || !org || !plan || active.length === 0,
				}}
			/>
		</ChipModal>
	);
}
